import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const ApplyHero = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  // Parallax transforms - DESKTOP ONLY
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.5, 0.8], [1, 0.5, 0]);
  const glowIntensity = useTransform(scrollYProgress, [0, 0.5], [0.08, 0.15]);

  return (
    <section 
      ref={containerRef}
      className="relative min-h-[75vh] lg:min-h-[100vh] flex items-center justify-center overflow-hidden"
    >
      {/* Dynamic Background Glow - DESKTOP ONLY */}
      <motion.div 
        className="hidden lg:block absolute inset-0 pointer-events-none"
        style={{ opacity: glowIntensity }}
      >
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-accent-blue rounded-full blur-[120px]"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] bg-accent-blue rounded-full blur-[120px]"></div>
      </motion.div>

      {/* Static Background Glow - MOBILE */}
      <div className="lg:hidden absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-accent-blue/8 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] bg-accent-blue/8 rounded-full blur-[120px]"></div>
      </div>

      {/* DESKTOP - Scroll-animated content */}
      <motion.div 
        className="hidden lg:block relative z-10 max-w-[900px] mx-auto px-6 md:px-8 text-center"
        style={{ 
          y: contentY,
          opacity: contentOpacity
        }}
      >
        {/* Eyebrow */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-sm md:text-base text-accent-blue font-semibold mb-4 md:mb-6 uppercase tracking-wider"
        >
          Apply Now
        </motion.p>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 md:mb-8 leading-tight"
        >
          Get a Growth Bottleneck Audit
        </motion.h1>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="text-base md:text-lg lg:text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed"
        >
          Tell us about your company and we'll identify the structural constraints limiting your growth
        </motion.p>
      </motion.div>

      {/* MOBILE - Simple fade-in content, NO PARALLAX */}
      <div className="lg:hidden relative z-10 max-w-[900px] mx-auto px-5 text-center py-20">
        {/* Eyebrow */}
        <motion.p
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6 }}
          className="text-[13px] text-accent-blue font-semibold mb-5 uppercase tracking-widest"
        >
          Apply Now
        </motion.p>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-[36px] leading-[1.15] font-bold text-white mb-6"
        >
          Get a Growth Bottleneck Audit
        </motion.h1>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-[17px] leading-[1.7] text-gray-400 max-w-2xl mx-auto"
        >
          Tell us about your company and we'll identify the structural constraints limiting your growth
        </motion.p>
      </div>
    </section>
  );
};

export default ApplyHero;